import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChartCard } from "@/components/ReportCharts";
import RichTextEditor from "@/components/RichTextEditor";
import {
  ArrowLeft, PencilSimple, FloppyDisk, FilePdf, FileDoc, X, CircleNotch,
  CheckCircle, ClockCounterClockwise, ArrowCounterClockwise, Spinner, WarningCircle, Image as ImageIcon,
} from "@phosphor-icons/react";

function renderContent(text) {
  const lines = (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const blocks = [];
  let tableRows = [];
  const flush = () => {
    if (tableRows.length) {
      const rows = tableRows.map((r) => r.split("|").map((c) => c.trim()));
      const maxc = Math.max(...rows.map((r) => r.length));
      blocks.push({ type: "table", rows: rows.map((r) => [...r, ...Array(maxc - r.length).fill("")]) });
      tableRows = [];
    }
  };
  lines.forEach((ln) => {
    if (ln.includes(" | ") || (ln.match(/\|/g) || []).length >= 2) { tableRows.push(ln); return; }
    flush();
    if (/^source\s*:/i.test(ln)) blocks.push({ type: "source", text: ln });
    else if (ln.startsWith("- ") || ln.startsWith("• ")) blocks.push({ type: "bullet", text: ln.replace(/^[-•]\s*/, "") });
    else blocks.push({ type: "p", text: ln });
  });
  flush();
  return blocks;
}

function looksHtml(text) {
  return /<(table|p|ul|ol|h[1-6]|strong|em|br|div)\b/i.test((text || "").trim());
}

function Block({ b }) {
  if (b.type === "table")
    return (
      <div className="overflow-x-auto my-3">
        <table className="report-table">
          <thead><tr>{b.rows[0].map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
          <tbody>{b.rows.slice(1).map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci}>{c}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  if (b.type === "source") return <p className="report-source text-[13px] mt-2">{b.text}</p>;
  if (b.type === "bullet") return <li className="ml-5 list-disc text-[15px] leading-relaxed text-slate-800 my-1">{b.text}</li>;
  return <p className="text-[15px] leading-relaxed text-slate-800 my-2">{b.text}</p>;
}

function GeneratingView({ report }) {
  const p = report.gen_progress || { total: 0, done: 0, stage: "" };
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
  return (
    <div data-testid="generating-view">
      <div className="card p-6">
        <div className="flex items-center gap-3">
          <Spinner size={26} className="spin-slow text-[#F59E0B]" />
          <div>
            <div className="text-slate-900 font-semibold">Generating report…</div>
            <div className="text-xs text-slate-500">{p.stage || "Working…"}</div>
          </div>
          <div className="ml-auto kpi-value text-3xl font-bold text-[#004182]">{pct}%</div>
        </div>
        <div className="h-2.5 rounded-full bg-slate-100 mt-4 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#F59E0B] to-[#f6ad3d] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[11px] text-slate-400 mt-2">{p.done} / {p.total} steps complete</div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-2">
        {report.sections.map((s) => (
          <div key={s.id} data-testid={`gen-section-${s.id}`} className="card px-4 py-3 flex items-center gap-3">
            {s.status === "done" ? <CheckCircle size={18} weight="fill" className="text-emerald-500" />
              : s.status === "failed" ? <WarningCircle size={18} weight="fill" className="text-red-500" />
              : s.status === "running" ? <CircleNotch size={18} className="spin-slow text-[#F59E0B]" />
              : <div className="h-[18px] w-[18px] rounded-full border-2 border-slate-300" />}
            <span className={`text-sm ${s.status === "done" ? "text-slate-800" : s.status === "running" ? "text-[#b45309] font-medium" : "text-slate-400"}`}>{s.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ReportViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);
  const pollRef = useRef(null);

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: img } = await api.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/reports/${id}`, { cover_image: img.id });
      await load(); loadVersions();
      toast.success("Cover image added");
    } catch { toast.error("Image upload failed"); }
    finally { setCoverUploading(false); if (coverInputRef.current) coverInputRef.current.value = ""; }
  };

  const load = async () => {
    const { data } = await api.get(`/reports/${id}`);
    setReport(data); setDraft(data);
    return data;
  };
  const loadVersions = () => api.get(`/reports/${id}/versions`).then((r) => setVersions(r.data)).catch(() => {});

  useEffect(() => { load(); loadVersions(); return () => clearInterval(pollRef.current); }, [id]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (report && report.status === "generating") {
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d.status !== "generating") { clearInterval(pollRef.current); toast.success("Report ready"); }
      }, 1600);
    }
    return () => clearInterval(pollRef.current);
  }, [report?.status]); // eslint-disable-line

  if (!report) return <div className="text-slate-500">Loading report…</div>;

  if (report.status === "generating") {
    return (
      <div className="space-y-5">
        <button onClick={() => navigate("/history")} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm" data-testid="viewer-back">
          <ArrowLeft size={18} /> History
        </button>
        <GeneratingView report={report} />
      </div>
    );
  }

  const updateSection = (idx, content) =>
    setDraft((d) => ({ ...d, sections: d.sections.map((s, i) => (i === idx ? { ...s, content } : s)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/reports/${id}`, { title: draft.title, sections: draft.sections });
      toast.success("Report updated · new version saved");
      setReport(draft); setEditing(false); loadVersions();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const restore = async (vno) => {
    if (!window.confirm(`Restore version v${vno}? Current state is saved as a new version.`)) return;
    const { data } = await api.post(`/reports/${id}/restore/${vno}`);
    setReport(data); setDraft(data); loadVersions();
    toast.success(`Restored v${vno}`);
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/reports/${id}/export/${fmt}`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = `${report.title.slice(0, 40)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const data = editing ? draft : report;
  const failedCount = (report.sections || []).filter((s) => s.source_failed).length;
  const genDate = report.generated_at || report.created_at;
  const daysSinceGen = genDate ? Math.floor((Date.now() - new Date(genDate).getTime()) / 86400000) : 0;
  const editable = report.status !== "completed" || !genDate || daysSinceGen < 15;
  const fmtGen = genDate ? new Date(genDate).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : "—";

  const Header = (
    <div className="flex items-center justify-between gap-4 border-b-2 border-slate-200 pb-5">
      <div className="flex items-center gap-4">
        <img src="/gail-logo.png" alt="GAIL" className="h-14 w-auto" />
        <div className="text-[#004182] font-semibold text-base leading-tight">{data.department}</div>
      </div>
      <div className="text-right text-[11px] text-slate-400 italic">For internal use only</div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="report-viewer-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm" data-testid="viewer-back">
          <ArrowLeft size={18} /> Back
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="versions-btn" onClick={() => setShowVersions((v) => !v)} className="bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg gap-2 h-10">
            <ClockCounterClockwise size={16} /> Versions ({versions.length})
          </Button>
          {!editing ? (
            editable ? (
              <Button data-testid="edit-report-btn" onClick={() => setEditing(true)} className="bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg gap-2 h-10"><PencilSimple size={16} /> Edit</Button>
            ) : (
              <span data-testid="edit-locked-note" className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 h-10">
                <WarningCircle size={15} weight="fill" className="text-amber-500" /> Editing closed (older than 15 days)
              </span>
            )
          ) : (
            <>
              <Button data-testid="cancel-edit-btn" variant="ghost" onClick={() => { setDraft(report); setEditing(false); }} className="text-slate-500 gap-2 h-10"><X size={16} /> Cancel</Button>
              <Button data-testid="save-report-btn" onClick={save} disabled={saving} className="gold-btn rounded-lg gap-2 h-10 px-4"><FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save"}</Button>
            </>
          )}
          <Button data-testid="export-docx-btn" onClick={() => download("docx")} className="bg-[#004182] hover:bg-[#003466] text-white rounded-lg gap-2 h-10"><FileDoc size={16} /> Word</Button>
          <Button data-testid="export-pdf-btn" onClick={() => download("pdf")} className="bg-[#b91c1c] hover:bg-[#991b1b] text-white rounded-lg gap-2 h-10"><FilePdf size={16} /> PDF</Button>
        </div>
      </div>

      {failedCount > 0 && (
        <div data-testid="failed-banner" className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <WarningCircle size={22} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">
            <span className="font-semibold">{failedCount} section{failedCount > 1 ? "s" : ""} flagged.</span> Live source data could not be fetched or AI generation failed for these sections — they are marked below and rely on prior knowledge. Verify before circulation.
          </div>
        </div>
      )}

      {showVersions && (
        <div className="card p-4" data-testid="versions-panel">
          <div className="text-sm font-semibold text-slate-900 mb-3">Version History (current: v{report.version})</div>
          {versions.length === 0 && <div className="text-sm text-slate-400">No previous versions yet. Edits create versions automatically.</div>}
          <div className="space-y-2">
            {versions.map((v) => (
              <div key={v._id} data-testid={`version-${v.version_no}`} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5">
                <div>
                  <span className="text-sm text-slate-800 font-medium">v{v.version_no}</span>
                  <span className="text-xs text-slate-500 ml-3">{v.note}</span>
                  <span className="text-xs text-slate-400 ml-3">{new Date(v.created_at).toLocaleString("en-IN")}</span>
                </div>
                <Button data-testid={`restore-${v.version_no}`} onClick={() => restore(v.version_no)} className="bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-lg h-8 gap-1.5 text-xs">
                  <ArrowCounterClockwise size={14} /> Restore
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="report-canvas rounded-xl p-4 sm:p-10 space-y-8 flex flex-col items-center">
        {/* PAGE 1 — COVER */}
        <div className="report-paper w-full max-w-[820px] rounded-sm px-6 sm:px-14 py-16 min-h-[70vh] flex flex-col" data-testid="report-cover">
          {Header}
          <div className="tricolor-bar h-1.5 rounded-full mt-6 w-2/3 mx-auto" />
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            {data.cover_image && (
              <img src={`${API}/images/${data.cover_image}`} alt="Cover" data-testid="cover-image"
                className="w-full max-w-lg max-h-64 object-cover rounded-xl mb-8 shadow-md" />
            )}
            <div className="mb-6">
              <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={onCoverUpload} data-testid="cover-image-input" />
              <button onClick={() => coverInputRef.current?.click()} disabled={coverUploading} data-testid="add-cover-image-btn"
                className="inline-flex items-center gap-2 text-xs text-[#004182] bg-[#e6f0fb] hover:bg-[#d5e6f8] rounded-full px-3 py-1.5 font-medium transition-colors">
                {coverUploading ? <CircleNotch size={14} className="spin-slow" /> : <ImageIcon size={14} weight="duotone" />}
                {data.cover_image ? "Change cover image" : "Add cover image above heading"}
              </button>
            </div>
            {!data.cover_image && <img src="/gail-logo.png" alt="GAIL" className="h-24 w-auto mb-10" />}
            <h1 className="report-title text-3xl sm:text-4xl text-slate-900 font-bold leading-tight max-w-2xl">{data.title}</h1>
            <div className="mt-6 text-slate-600 text-base">
              Issue No - <span className="font-semibold">{data.issue_no || "—"}</span> &nbsp;|&nbsp; Issue Date: <span className="font-semibold">{data.issue_date || "—"}</span> &nbsp;|&nbsp; Version {data.version || 1}
            </div>
            <div className="mt-1.5 text-slate-500 text-sm" data-testid="cover-generated-date">Report Generated: <span className="font-medium">{fmtGen}</span></div>
            <div className="mt-3 text-slate-400 text-sm italic">For internal use only — GAIL (India) Limited</div>
          </div>
        </div>

        {/* PAGE 2+ — REPORT */}
        <div className="report-paper w-full max-w-[820px] rounded-sm px-6 sm:px-14 py-10 sm:py-14" data-testid="report-paper-wrap">
          <div className="space-y-8">
            {data.sections.map((s, idx) => {
              const chart = (data.charts || []).find((c) => c.section_id === s.id);
              return (
              <section key={s.id} data-testid={`report-section-${s.id}`}>
                <h2 className="report-section-title text-2xl font-semibold pb-1.5 mb-3 flex items-center gap-2">
                  {s.title}
                  {s.source_failed && (
                    <span data-testid={`section-flag-${s.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                      <WarningCircle size={12} weight="fill" /> Source issue
                    </span>
                  )}
                  {s.carried_forward && (
                    <span data-testid={`carried-flag-${s.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      <ClockCounterClockwise size={12} weight="fill" /> Carried forward{s.carried_from ? ` from ${s.carried_from}` : ""}
                    </span>
                  )}
                </h2>
                {s.source_failed && s.source_note && (
                  <p className="text-[12px] text-red-600 bg-red-50/60 border-l-2 border-red-300 pl-3 py-1 mb-2 italic">{s.source_note}</p>
                )}
                {chart && !s.data_unavailable && !editing && (
                  <div className="mb-4" data-testid={`section-chart-${s.id}`}><ChartCard chart={chart} light wide /></div>
                )}
                {s.data_unavailable ? (
                  <div data-testid={`data-unavailable-${s.id}`} className="flex items-center gap-2 text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-lg px-4 py-3 text-sm">
                    <WarningCircle size={18} weight="duotone" className="text-slate-400" /> DATA NOT AVAILABLE TILL NOW
                  </div>
                ) : (s.fmt === "html" || (!editing && looksHtml(s.content))) ? (
                  editing ? (
                    <RichTextEditor testid={`edit-html-${idx}`} value={s.content} onChange={(html) => updateSection(idx, html)} />
                  ) : (
                    <div data-testid={`report-html-${s.id}`} className="report-html" dangerouslySetInnerHTML={{ __html: s.content }} />
                  )
                ) : editing ? (
                  <Textarea data-testid={`edit-section-${idx}`} value={s.content}
                    onChange={(e) => updateSection(idx, e.target.value)}
                    className="min-h-[160px] text-slate-900 font-mono text-xs border-slate-300" />
                ) : s.carried_forward ? (
                  <div data-testid={`carried-content-${s.id}`} className="bg-amber-50/60 border-l-4 border-amber-300 rounded-r-lg pl-4 pr-3 py-2">
                    <ul>{renderContent(s.content).map((b, i) => <Block key={i} b={b} />)}</ul>
                  </div>
                ) : (
                  <div><ul>{renderContent(s.content).map((b, i) => <Block key={i} b={b} />)}</ul></div>
                )}
              </section>
              );
            })}
          </div>

          <div className="mt-10 pt-4 border-t border-slate-200 text-center text-[11px] text-slate-400">
            Sankalak · संकलक — GAIL (India) Limited · Corporate Strategy, Planning & Advocacy Department
          </div>
        </div>
      </div>
    </div>
  );
}
